import React, { Fragment, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import usePopper from './usePopper';

// TODO: This uses z-indexes, which ought to be globally defined.
const ClickOutsideArea = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
`;

const PopoverContainer = styled.div`
  transition: margin 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(-15px) translateX(-50%);
  background: #ffffff;
  border-radius: 4px;
  border: 1px solid #d9d9de;
  box-shadow: 0 0 6px rgba(0,0,0,0.2);
  color: rgba(0,0,0,0.6);
  left: 50%;
  cursor: pointer;
  pointer-events: none;
  width: 150px;
  padding: 0;
  position: fixed;
  z-index: 32;

  ${(props) =>
    props.show ? `
    margin-top: ${props['data-placement'] === 'bottom' ? 10 : -10}px;
    visibility: visible;
    pointer-events: auto;
    opacity: 1;
  ` : `
    pointer-events: none;
    margin-top: 0;
    opacity: 0;
    visibility: hidden;
  `}

  left: ${(props) => props.x}px;
  top: ${(props) => props.y}px;
  width: ${(props) => props.width}px;
`;

const Arrow = styled.div`
  position: absolute;
  height: 14px;
  width: 14px;
  transform: rotate(45deg) translateY(100%) translateX(100%);
  background-color: white;
  content: "";
  pointer-events: none;
  box-shadow: -2px -2px 5px rgba(0,0,0,0.2);
  transform: rotate(45deg) translateY(0) translateX(0);

  ${PopoverContainer}[data-placement = "bottom"] & {
    top: -7px;
  }
  ${PopoverContainer}[data-placement = "top"] & {
    bottom: -7px;
  }
`;

const Content = styled.div`
  background: white;
  border: none;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  border-radius: 4px;
  z-index: 10;
`;

/* A simple popover component that renders react node children
 * in a styled box underneath the supplied html element.
 * The popover is activated by altering the state of the show prop.
 * It will never dismiss itself, however it does accept an onclose
 * function callback to alter the parent's show state.
 */
const Popover = ({ el, children, show, width = 200, onClose }) => {
  const menuRef = useRef(null);
  const arrowRef = useRef(null);

  const { styles, placement, arrowStyles } = usePopper({
    referrenceNode: el,
    popperNode: menuRef.current,
    arrowNode: arrowRef.current,
    modifiers: {
      flip: {
        enabled: true,
        behavior: 'flip',
      },
      preventOverflow: {
        enabled: true,
        boundariesElement: 'window',
      },
    },
  });

  return (
    <Fragment>
      {show && <ClickOutsideArea onClick={onClose} />}
      <PopoverContainer width={width} show={show} aria-hidden={show} ref={menuRef} style={styles} data-placement={placement}>
        <Content>
          {children}
        </Content>
        <Arrow ref={arrowRef} style={arrowStyles} />
      </PopoverContainer>

    </Fragment>
  );
};

Popover.propTypes = {
  el: PropTypes.object,
  width: PropTypes.number,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.object,
};

export default Popover;
